// src/pages/personal/analytics/CustomerSection/hooks/useCustomerData.ts
import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBusinessId } from '@/hooks/useBusinessId';
import { api } from '@/services/api';
import { Period, DateRange } from '../../types/types';
// Импортируем процессор данных для графиков
// Импортируем форматтеры для обработки данных
import { formatSmartDate } from '@/utils/dateUtils';

// Константы для валидных значений
const VALID_LEAD_SOURCES = [
  'instagram', 'facebook', 'seo', 'maps', 'walk_in', 'referral', 'other'
];

interface UseCustomerDataParams {
  period: Period;
  dateRange?: DateRange;
  statuses?: string[];
  leadSources?: string[];
}

// Тип для метрики
interface Metric {
  value: number;
  change: number;
  trend: 'up' | 'down' | 'neutral';
}

// Тип для статусного распределения
interface StatusDistribution {
  status: string;
  name: string;
  count: number;
  percentage: number;
  change: number;
  trend: 'up' | 'down' | 'neutral';
}

// Тип для распределения по источникам
interface LeadSourceDistribution {
  source: string;
  name: string;
  count: number;
  percentage: number;
  change: number;
  trend: 'up' | 'down' | 'neutral';
  revenue: number;
}

// Тип для частоты заказов по статусам
interface FrequencyByStatus {
  status: string;
  name: string;
  average_frequency: number;
  clients_count: number;
}

// Тип для тренда новых клиентов с форматированной датой
interface NewClientsTrend {
  month: string; // '2023-01'
  month_name: string; // 'Jan 2023'
  formatted_month?: string; // Добавляем поле для предварительно форматированной даты
  count: number;
}

// Тип для топ-клиентов
interface TopClient {
  id: string;
  name: string;
  phone: string;
  status: string;
  status_name: string;
  total_spent: number;
  orders_count: number;
  period_total: number;
  period_orders_count: number;
}

// Полный тип для данных аналитики клиентов
interface CustomerAnalyticsData {
  metrics: {
    total_clients: Metric;
    new_clients: Metric;
    arpu: Metric;
    retention_rate: Metric;
    ltv: Metric;
    aov: Metric;  // Средняя стоимость заказа
    pf: Metric;   // Частота покупок
  };
  status_distribution: StatusDistribution[];
  leadsource_distribution: LeadSourceDistribution[];
  top_clients: TopClient[];
  status_changes: any[]; // Можно уточнить при необходимости
  frequency_by_status: FrequencyByStatus[];
  new_clients_trend: NewClientsTrend[];
  date_range?: {
    start: string;
    end: string;
  };
  timezone?: string;
  updated_at?: string;
  [key: string]: any;
}

interface UseCustomerDataReturn {
  data: CustomerAnalyticsData | null;
  isLoading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  lastUpdated?: string;
  dateRange?: DateRange;
  setDateRange: (range: DateRange) => void;
}

export const useCustomerData = ({
  period,
  dateRange,
  statuses = [],
  leadSources = []
}: UseCustomerDataParams): UseCustomerDataReturn => {
  const { t, i18n } = useTranslation(['customer', 'errors']);
  const currentLocale = i18n.language;
  const { businessId, isLoading: isBusinessIdLoading } = useBusinessId();
  
  const [data, setData] = useState<CustomerAnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | undefined>(undefined);
  const [localDateRange, setLocalDateRange] = useState<DateRange | undefined>(dateRange);
  
  // Флаг для контроля активности компонента
  const isMounted = useRef(true);
  // Флаг для отслеживания первого рендера
  const isFirstRender = useRef(true);
  // Предыдущее состояние параметров для сравнения
  const prevParams = useRef({ period, statuses, leadSources, businessId, dateRange });
  // Счетчик запросов для отладки
  const fetchCounter = useRef(0);

  // Функция для предварительной обработки данных
  const preprocessData = (responseData: any): CustomerAnalyticsData => {
    // Форматируем даты в данных о новых клиентах
    const processedData = { ...responseData };
    
    if (processedData.new_clients_trend && Array.isArray(processedData.new_clients_trend)) {
      processedData.new_clients_trend = processedData.new_clients_trend.map((item: NewClientsTrend) => {
        // Добавляем форматированную дату
        return {
          ...item,
          formatted_month: item.month_name || formatSmartDate(`${item.month}-01T00:00:00Z`, false, currentLocale)
        };
      });
    }
    
    // Сохраняем время последнего обновления
    if (responseData.updated_at) {
      setLastUpdated(responseData.updated_at);
    } else if (responseData.date_range) {
      // Если нет updated_at, но есть date_range, используем конечную дату
      setLastUpdated(responseData.date_range.end);
    } else {
      // Иначе используем текущее время
      const now = new Date().toISOString();
      setLastUpdated(now);
    }
    
    return processedData;
  };

  // Функция сравнения объектов для определения изменений
  const areParamsEqual = (prev: any, curr: any) => {
    // Проверка базовых полей
    if (prev.period !== curr.period) {
      console.log('Изменился period:', prev.period, ' -> ', curr.period);
      return false;
    }
    
    // Проверка статусов
    if (prev.statuses.length !== curr.statuses.length) {
      console.log('Изменился размер списка статусов');
      return false;
    }
    for (let i = 0; i < prev.statuses.length; i++) {
      if (prev.statuses[i] !== curr.statuses[i]) {
        console.log('Изменился состав списка статусов');
        return false;
      }
    }
    
    // Проверка источников
    if (prev.leadSources.length !== curr.leadSources.length) {
      console.log('Изменился размер списка источников');
      return false;
    }
    for (let i = 0; i < prev.leadSources.length; i++) {
      if (prev.leadSources[i] !== curr.leadSources[i]) {
        console.log('Изменился состав списка источников');
        return false;
      }
    }
    
    if (prev.businessId !== curr.businessId) {
      console.log('Изменился businessId');
      return false;
    }
    
    // Сравнение диапазонов дат
    if (prev.dateRange && curr.dateRange) {
      // Проверяем существование дат и их валидность
      const prevStartValid = prev.dateRange.startDate instanceof Date && 
                            !isNaN(prev.dateRange.startDate.getTime());
      const prevEndValid = prev.dateRange.endDate instanceof Date && 
                          !isNaN(prev.dateRange.endDate.getTime());
      const currStartValid = curr.dateRange.startDate instanceof Date && 
                            !isNaN(curr.dateRange.startDate.getTime());
      const currEndValid = curr.dateRange.endDate instanceof Date && 
                          !isNaN(curr.dateRange.endDate.getTime());
      
      // Если какая-то из дат недействительна, считаем, что параметры изменились
      if (!prevStartValid || !prevEndValid || !currStartValid || !currEndValid) {
        console.log('Одна из дат недействительна');
        return false;
      }
      
      // Сравниваем даты
      if (prev.dateRange.startDate.getTime() !== curr.dateRange.startDate.getTime()) {
        console.log('Изменилась начальная дата:', 
          prev.dateRange.startDate.toISOString(), ' -> ', 
          curr.dateRange.startDate.toISOString());
        return false;
      }
      
      if (prev.dateRange.endDate.getTime() !== curr.dateRange.endDate.getTime()) {
        console.log('Изменилась конечная дата:', 
          prev.dateRange.endDate.toISOString(), ' -> ', 
          curr.dateRange.endDate.toISOString());
        return false;
      }
    } else if ((prev.dateRange && !curr.dateRange) || (!prev.dateRange && curr.dateRange)) {
      console.log('Изменилось наличие dateRange');
      return false;
    }
    
    console.log('Все параметры одинаковые');
    return true;
  };

  const fetchData = useCallback(async () => {
    // Увеличиваем счетчик запросов для отладки
    fetchCounter.current += 1;
    const requestId = fetchCounter.current;
    
    console.log(`[${requestId}] Запрос данных клиентов начат`);
    
    if (isBusinessIdLoading || !businessId || !isMounted.current) {
      console.log(`[${requestId}] Запрос отменен: businessId загружается или компонент размонтирован`);
      return;
    }

    // Проверяем валидность dateRange
    const isDateRangeValid = localDateRange && 
      localDateRange.startDate instanceof Date && !isNaN(localDateRange.startDate.getTime()) &&
      localDateRange.endDate instanceof Date && !isNaN(localDateRange.endDate.getTime());

    // Формируем текущие параметры
    const currentParams = { 
      period, 
      statuses, 
      leadSources,
      dateRange: isDateRangeValid ? localDateRange : undefined,
      businessId 
    };
    
    // Логируем текущие параметры
    console.log(`[${requestId}] Текущие параметры:`, {
      period,
      statuses,
      leadSources,
      dateRange: isDateRangeValid ? {
        startDate: localDateRange.startDate.toISOString(),
        endDate: localDateRange.endDate.toISOString()
      } : undefined,
      isFirstRender: isFirstRender.current
    });
    
    // Если не первый рендер, проверяем изменение параметров
    if (!isFirstRender.current) {
      console.log(`[${requestId}] Проверка изменения параметров`);
      
      // Если параметры не изменились, пропускаем запрос
      if (areParamsEqual(prevParams.current, currentParams)) {
        console.log(`[${requestId}] Параметры не изменились, пропускаем запрос`);
        return;
      }
    }
    
    // Сбрасываем флаг первого рендера
    isFirstRender.current = false;
    
    setIsLoading(true);
    setError(null);

    try {
      // Формируем параметры запроса
      const params: any = { 
        business: businessId.toString(),
        period: period,
        // Отправляем часовой пояс клиента
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      // Проверка, что период валидный
      if (!['day', 'week', 'month', 'quarter', 'year', 'custom'].includes(period)) {
        console.warn(`Некорректный период: ${period}, используем 'month' по умолчанию`);
        params.period = 'month';
      }

      // Если есть диапазон дат и он валидный, добавляем его
      if (isDateRangeValid) {
        // Отправляем ISO строки для корректной обработки на бэкенде
        params.start_date = localDateRange.startDate.toISOString();
        params.end_date = localDateRange.endDate.toISOString();
        
        console.log(`Отправляем даты: start=${params.start_date}, end=${params.end_date}, timezone=${params.timezone}`);
      } else if (params.period === 'custom') {
        // Для custom периода обязательно нужны даты
        console.warn('Для периода custom требуется валидный dateRange, используем period=month');
        params.period = 'month';
      }
      
      // Добавляем фильтры если они указаны
      if (statuses.length > 0) {
        params.status = statuses.join(',');
      }
      
      // Фильтруем, оставляя только валидные значения источников
      const validLeadSources = leadSources.filter(source => 
        VALID_LEAD_SOURCES.includes(source)
      );
      
      if (validLeadSources.length > 0) {
        params.leadsource = validLeadSources.join(',');
      }
      
      console.log(`[${requestId}] Запрос к API с параметрами:`, params);
      
      // Запрос к API
      const response = await api.get('/api/analytics/customers/', params);

      // Проверяем активность компонента
      if (!isMounted.current) {
        console.log(`[${requestId}] Компонент размонтирован, отменяем обработку ответа`);
        return;
      }

      console.log(`[${requestId}] Получен ответ от API`, 
        response ? 'с данными' : 'без данных');

      // Обновляем prevParams только после успешного запроса
      prevParams.current = currentParams;

      if (response) {
        // Предварительная обработка и форматирование данных
        const processedData = preprocessData(response);
        setData(processedData);
      } else {
        setError(t('errors:data_loading_error'));
      }
    } catch (error: any) {
      console.error('Error fetching customer data:', error);
      setError(error.message || t('errors:data_loading_error'));
    } finally {
      setIsLoading(false);
    }
  }, [businessId, isBusinessIdLoading, period, statuses, leadSources, localDateRange, t, currentLocale]);

  // Обработчик изменения диапазона дат
  const updateDateRange = useCallback((range: DateRange) => {
    console.log('updateDateRange вызван:', {
      startDate: range.startDate.toISOString(),
      endDate: range.endDate.toISOString()
    });
    
    // Проверка на валидность дат
    const isStartDateValid = range.startDate instanceof Date && !isNaN(range.startDate.getTime());
    const isEndDateValid = range.endDate instanceof Date && !isNaN(range.endDate.getTime());
    
    if (!isStartDateValid || !isEndDateValid) {
      console.error('Получены невалидные даты:', range);
      return;
    }
    
    // Проверка на изменение диапазона дат
    const isNewRange = !localDateRange || 
        range.startDate.getTime() !== localDateRange.startDate.getTime() || 
        range.endDate.getTime() !== localDateRange.endDate.getTime();
    
    if (isNewRange) {
      console.log('Диапазон дат изменился, обновляем');
      setLocalDateRange(range);
    } else {
      console.log('Диапазон дат не изменился, пропускаем обновление');
    }
  }, [localDateRange]);

  // Загружаем данные при изменении зависимостей
  useEffect(() => {
    console.log('Монтирование компонента, настройка параметров');
    isMounted.current = true;
    
    // Запускаем первичную загрузку данных
    fetchData();
    
    return () => {
      console.log('Размонтирование компонента');
      isMounted.current = false;
    };
  }, [fetchData]);

  return {
    data,
    isLoading,
    error,
    refreshData: fetchData,
    lastUpdated,
    dateRange: localDateRange,
    setDateRange: updateDateRange
  };
};